/**
 * Ce que l'organisateur reçoit pour le prix qu'il saisit, et le calcul inverse.
 *
 * Les montants viennent du serveur (GET /events/{id}/pricing-quote), qui
 * applique la même règle que le paiement (cloud-api/api/core/pricing.py) :
 * l'écran ne calcule rien, il ne peut donc pas annoncer un autre montant que
 * celui qui sera encaissé.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiService from '../services/api.service';
import { Button } from './ui/button';
import { NumberInput } from './ui/number-input';

export type CommissionPayer = 'athlete' | 'organizer';

interface Quote {
  paid_cents: number;
  received_cents: number;
  commission_cents: number;
  too_low: boolean;
  minimum_price_cents: number;
  price_to_enter_cents?: number;
}

interface PriceQuoteProps {
  eventId: string;
  payer: CommissionPayer;
  /** Prix saisi, en euros. */
  price: number;
  /** Remplit le champ prix avec le résultat du calcul inverse (en euros). */
  onUsePrice: (euros: number) => void;
}

const euros = (cents: number) =>
  (cents / 100).toLocaleString(undefined, { style: 'currency', currency: 'EUR' });

const PriceQuote: React.FC<PriceQuoteProps> = ({ eventId, payer, price, onUsePrice }) => {
  const { t } = useTranslation();
  const [quote, setQuote] = useState<Quote | null>(null);
  // Calcul inverse replié par défaut : la ligne « Tu reçois » suffit le plus
  // souvent. Ouvert, il part de ce que l'organisateur reçoit déjà.
  const [inverseOpen, setInverseOpen] = useState(false);
  const [want, setWant] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);

  const priceCents = Math.round((price || 0) * 100);
  const wantCents = Math.round((want || 0) * 100);

  useEffect(() => {
    if (priceCents <= 0) {
      setQuote(null);
      return;
    }
    // Une réponse lente ne doit pas écraser celle d'un prix tapé après elle.
    let annule = false;
    const timer = setTimeout(async () => {
      try {
        const params: Record<string, number> = { price_cents: priceCents };
        if (payer === 'organizer' && inverseOpen && wantCents > 0) params.receive_cents = wantCents;
        const res = await apiService.get(`/events/${eventId}/pricing-quote`, params);
        if (annule) return;
        setQuote(res.data);
        setError(null);
      } catch (err: any) {
        if (annule) return;
        setQuote(null);
        setError(err?.response?.data?.detail || t('commission.quoteError'));
      }
    }, 250);
    return () => {
      clearTimeout(timer);
      annule = true;
    };
  }, [eventId, payer, priceCents, wantCents, inverseOpen, t]);

  if (priceCents <= 0) return null;

  return (
    <div className="space-y-3">
      {error && <p className="text-sm text-destructive">{error}</p>}
      {quote && (
        <div className="rounded-md bg-muted/40 px-3 py-2 text-sm space-y-1">
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">{t('commission.pageShows')}</span>
            <span>
              {euros(priceCents)}{' '}
              <span className="text-muted-foreground">
                {payer === 'organizer' ? t('commission.allIncluded') : t('commission.plusFees')}
              </span>
            </span>
          </div>
          <div className="flex justify-between gap-4 border-t border-border pt-1">
            <span className="font-medium">{t('commission.youReceive')}</span>
            <span className="text-base font-semibold">
              {euros(Math.max(0, quote.received_cents))}
            </span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">{t('commission.athletePays')}</span>
            <span className="text-muted-foreground">{euros(quote.paid_cents)}</span>
          </div>
          <div className="flex justify-between gap-4 text-xs">
            <span className="text-muted-foreground">{t('commission.commissionLine')}</span>
            <span className="text-muted-foreground">{euros(quote.commission_cents)}</span>
          </div>
        </div>
      )}
      {quote?.too_low && (
        <p className="text-sm text-destructive">
          {t('commission.tooLow', { min: euros(quote.minimum_price_cents) })}
        </p>
      )}
      {payer === 'organizer' && !inverseOpen && (
        <button
          type="button"
          className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
          onClick={() => {
            setWant(quote ? Math.max(0, quote.received_cents) / 100 : 0);
            setInverseOpen(true);
          }}
        >
          {t('commission.showInverse')}
        </button>
      )}
      {payer === 'organizer' && inverseOpen && (
        <div className="space-y-2 border-t border-border pt-3">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-muted-foreground">{t('commission.iWantToReceive')}</span>
            <NumberInput
              step="0.01"
              min={0}
              value={want}
              onChange={e => setWant(parseFloat(e.target.value) || 0)}
              className="w-28"
              autoFocus
            />
            <span className="text-muted-foreground">€</span>
          </div>
          {quote?.price_to_enter_cents != null && (
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <span>
                {t('commission.priceToEnter')}{' '}
                <span className="font-semibold">{euros(quote.price_to_enter_cents)}</span>
              </span>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => {
                  onUsePrice(quote.price_to_enter_cents! / 100);
                  setInverseOpen(false);
                }}
              >
                {t('commission.usePrice')}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PriceQuote;
